import { Data, Schema } from "effect";
import type { ParseIssue } from "effect/ParseResult";
import { CurrencySchema, type Currency } from "./Currency";

/**
 * A business rule says: we don't support companies with too much revenue.
 *
 * This cap is at 10M.
 */
const hardCappedMaxMoneyValue = 10_000_000;

export class InvalidMoneyCurrencies extends Data.TaggedError("InvalidMoneyCurrencies")<{
  readonly given: Currency;
  readonly other: Currency;
  readonly message?: string;
}> {}

/**
 * Money represents an amount of money in a given currency
 */
export class MoneySchema extends Schema.TaggedClass<MoneySchema>("@@Money")("Money", {
  currency: CurrencySchema,
  amount: Schema.Number.pipe(
    Schema.greaterThanOrEqualTo(0, {
      message: ({ actual: amount }) => `Amount must be non-negative, but is: ${amount}`,
    }),
    Schema.lessThanOrEqualTo(hardCappedMaxMoneyValue, {
      message: ({ actual }: ParseIssue) =>
        `You are too rich to be here. Eat the rich. ${JSON.stringify(actual as number)}`,
    })
  ),
}) {
  /**
   * Creates a new MoneySchema instance representing an amount in euros.
   * @param amount The amount in euros.
   * @returns A new MoneySchema instance.
   */
  static EURO(amount: number) {
    return new MoneySchema({
      amount,
      currency: CurrencySchema.euro,
    });
  }

  static fromNumeric(amount: number, currency: CurrencySchema) {
    return new MoneySchema({
      amount,
      currency,
    });
  }

  add(other: MoneySchema): MoneySchema {
    this.assertSameCurrency(other);
    return new MoneySchema({
      ...this,
      amount: this.amount + other.amount,
    });
  }

  addMultiple(...others: MoneySchema[]): MoneySchema {
    let finalAmount = this.amount;
    for (const other of others) {
      this.assertSameCurrency(other);
      finalAmount += other.amount;
    }

    return new MoneySchema({
      currency: this.currency,
      amount: finalAmount,
    });
  }

  addNumeric(amount: number): MoneySchema {
    return {
      ...this,
      amount: this.amount + amount,
    } as MoneySchema;
  }

  subtract(other: MoneySchema): MoneySchema {
    this.assertSameCurrency(other);
    return new MoneySchema({
      ...this,
      amount: this.amount - other.amount,
    });
  }

  subtractNumeric(amount: number): MoneySchema {
    return new MoneySchema({
      ...this,
      amount: this.amount - amount,
    });
  }

  multiply(factor: number): MoneySchema {
    return new MoneySchema({
      ...this,
      amount: this.amount * factor,
    });
  }

  /**
   * Divides the current amount by the given divisor.
   *
   * **NOTE**: because it's money, it ALWAYS rounds to two decimals!
   * @param divisor The divisor to divide the current amount
   * @returns
   */
  divide(divisor: number): MoneySchema {
    return new MoneySchema({
      ...this,
      amount: this.roundTwoDecimalsRaw(this.amount / divisor),
    });
  }

  roundTwoDecimals(): MoneySchema {
    return new MoneySchema({
      ...this,
      amount: this.roundTwoDecimalsRaw(this.amount),
    });
  }

  isLess(than: MoneySchema, orEqual?: "orEqual" | undefined): boolean {
    this.assertSameCurrency(than);
    if (orEqual === "orEqual") {
      return this.amount <= than.amount;
    }
    return this.amount < than.amount;
  }

  isLessNumeric(than: number, orEqual?: "orEqual" | undefined): boolean {
    if (orEqual === "orEqual") {
      return this.amount <= than;
    }
    return this.amount < than;
  }

  isMore(than: MoneySchema, orEqual?: "orEqual" | undefined): boolean {
    this.assertSameCurrency(than);
    if (orEqual === "orEqual") {
      return this.amount >= than.amount;
    }
    return this.amount > than.amount;
  }

  isMoreNumeric(than: number, orEqual?: "orEqual" | undefined): boolean {
    if (orEqual === "orEqual") {
      return this.amount >= than;
    }
    return this.amount > than;
  }

  private assertSameCurrency(other: MoneySchema) {
    if (this.currency.code !== other.currency.code) {
      throw new InvalidMoneyCurrencies({
        given: this.currency,
        other: other.currency,
      });
    }
  }

  private roundTwoDecimalsRaw(num: number) {
    return Math.round(num * 100) / 100;
  }
}

export type Money = typeof MoneySchema.Type;
